<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import PostsList from '../components/PostsList.vue';
import PostDetail from '../components/PostDetail.vue';
import PostComposer from '../components/PostComposer.vue';
import { useSocialPostsStore } from 'dashboard/stores/socialPosts';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const postsStore = useSocialPostsStore();
const { records, scheduledRecords, uiFlags } = storeToRefs(postsStore);

// Same inbox source CommentsFilters uses via CommentsIndex -- the composer
// only offers Facebook inboxes since publishing is a Facebook-page action.
const getInboxes = useMapGetter('inboxes/getInboxes');
const facebookInboxes = computed(() =>
  getInboxes.value.filter(inbox => inbox.channel_type === INBOX_TYPES.FB)
);
const inboxName = inboxId => {
  const inbox = facebookInboxes.value.find(i => i.id === inboxId);
  return inbox?.name || t('COMMENTS.POSTS.UNKNOWN_PAGE');
};

// The index endpoint accepts either flag (see SocialPostsController#ensure_readable). With
// neither, a hand-typed /comments URL would otherwise fire two requests the API answers 403.
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const canListPosts = computed(
  () =>
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.COMMENT_MANAGEMENT
    ) ||
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.POST_PUBLISHING
    )
);

// The composer (and this screen's own Create post button) gate on this narrower
// flag -- the empty state shouldn't point at a button the account can't actually see.
const canCreatePost = computed(() =>
  isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.POST_PUBLISHING
  )
);

// Same first-load-only skeleton rule CommentsIndex uses for its queue: only
// treat the empty-list fetch as loading, so a later refetch doesn't blank out
// a list the admin is currently looking at.
const isLoadingPublished = computed(
  () => uiFlags.value.fetchingList && records.value.length === 0
);
const isLoadingScheduled = computed(
  () =>
    uiFlags.value.fetchingScheduledList && scheduledRecords.value.length === 0
);
const isLoadingAny = computed(
  () => isLoadingPublished.value || isLoadingScheduled.value
);

const fetchPosts = () => {
  postsStore.get({ scheduled: false });
  postsStore.get({ scheduled: true });
};

// Scheduled and published posts render in a single rail filtered by the
// pill row above it -- each row is enriched here with the page name (real
// data looked up off the Facebook inbox list, never fabricated) and whether
// it is scheduled, since the merged list can no longer rely on which
// section it came from.
const combinedPosts = computed(() => [
  ...scheduledRecords.value.map(post => ({
    ...post,
    isScheduled: true,
    pageName: inboxName(post.inboxId),
  })),
  ...records.value.map(post => ({
    ...post,
    isScheduled: false,
    pageName: inboxName(post.inboxId),
  })),
]);

const FILTER_PILLS = ['all', 'published', 'scheduled'];
const activeFilter = ref('all');
const filteredPosts = computed(() => {
  if (activeFilter.value === 'published') {
    return combinedPosts.value.filter(post => !post.isScheduled);
  }
  if (activeFilter.value === 'scheduled') {
    return combinedPosts.value.filter(post => post.isScheduled);
  }
  return combinedPosts.value;
});

// The right pane must never sit blank while there is a post to show: the most
// recent published post is the common case, but a brand-new page with only a
// scheduled post still gets a populated detail pane rather than an empty one.
// This is independent of the active filter pill, which only narrows the rail.
const selectedId = ref(null);

const selectFirstAvailable = () => {
  const firstPublished = combinedPosts.value.find(post => !post.isScheduled);
  const firstScheduled = combinedPosts.value.find(post => post.isScheduled);
  selectedId.value = (firstPublished || firstScheduled)?.id ?? null;
};

watch(
  [records, scheduledRecords],
  () => {
    if (selectedId.value !== null) return;
    selectFirstAvailable();
  },
  { immediate: true }
);

const selectedPost = computed(
  () => combinedPosts.value.find(post => post.id === selectedId.value) || null
);

const handleSelect = id => {
  selectedId.value = id;
};

const composerRef = ref(null);
const openComposer = () => composerRef.value?.open();
defineExpose({ openComposer });

// Cancelling and publishing are both Graph writes that can fail, and the store rethrows Meta's
// own message. Surfacing it is the difference between "Facebook rejected this" and a scheduled
// post that appears to have been cancelled but has not. The composer is only cleared once the
// post actually exists -- clearing on click destroyed a fully composed post, image and all.
const handleCancel = async id => {
  try {
    await postsStore.delete(id);
    if (selectedId.value === id) selectFirstAvailable();
  } catch (error) {
    useAlert(error.message || t('COMMENTS.POSTS.CANCEL_ERROR'));
  }
};

const handleSubmit = async payload => {
  try {
    const post = await postsStore.create(payload);
    composerRef.value?.onSuccess();
    selectedId.value = post.id;
  } catch (error) {
    const fallback = payload.scheduledAt
      ? t('COMMENTS.POSTS.SCHEDULE_SUBMIT_ERROR')
      : t('COMMENTS.POSTS.PUBLISH_ERROR');
    useAlert(error.message || fallback);
  }
};

// The account record — and therefore its feature flags — can land after this
// component mounts. A one-shot mount-time guard reads `false` on a cold load and
// never retries, so the list stays empty even though the API would answer. (The
// sidebar entry recovers on its own because it is a computed in a template; a
// mount hook does not.) Watch the flag and fetch the first time it is true.
let hasFetched = false;
watch(
  canListPosts,
  enabled => {
    if (!enabled || hasFetched) return;

    hasFetched = true;
    fetchPosts();
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col w-full h-full gap-4 px-6 pt-5 pb-4 overflow-hidden">
    <div class="flex items-center gap-4">
      <div class="flex-1 min-w-0">
        <h1 class="text-3xl font-medium tracking-tight text-n-slate-12">
          {{ t('COMMENTS.POSTS.TITLE') }}
        </h1>
        <p class="mt-1 text-sm text-n-slate-11">
          {{
            t('COMMENTS.POSTS.SUBTITLE', {
              published: records.length,
              scheduled: scheduledRecords.length,
            })
          }}
        </p>
      </div>
      <Button
        v-if="canCreatePost"
        data-testid="create-post-button"
        icon="i-lucide-plus"
        :label="t('COMMENTS.POSTS.CREATE_BUTTON')"
        @click="openComposer"
      />
    </div>
    <div class="flex flex-col flex-1 min-h-0 gap-4 md:flex-row">
      <aside
        class="flex flex-col flex-none min-h-0 gap-3 max-h-64 w-full md:max-h-none md:w-[336px]"
      >
        <div class="flex items-center gap-1.5">
          <button
            v-for="pill in FILTER_PILLS"
            :key="pill"
            type="button"
            data-testid="posts-filter-pill"
            :data-filter="pill"
            class="h-7 px-3.5 text-xs font-medium rounded-full border"
            :class="
              activeFilter === pill
                ? 'bg-n-brand text-white border-transparent'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="activeFilter = pill"
          >
            {{ t(`COMMENTS.POSTS.FILTERS.${pill.toUpperCase()}`) }}
          </button>
        </div>
        <PostsList
          data-testid="posts-list"
          :posts="filteredPosts"
          :is-loading="isLoadingAny"
          :selected-id="selectedId"
          class="flex-1 min-h-0"
          @select="handleSelect"
        />
      </aside>
      <section
        class="flex-1 min-h-0 min-w-0 overflow-y-auto rounded-2xl bg-n-solid-1"
      >
        <PostDetail
          :post="selectedPost"
          :is-scheduled="selectedPost?.isScheduled ?? false"
          :is-loading="isLoadingAny"
          :can-create-post="canCreatePost"
          @cancel="handleCancel"
          @create="openComposer"
        />
      </section>
    </div>
  </div>
  <PostComposer
    ref="composerRef"
    :inboxes="facebookInboxes"
    :is-submitting="uiFlags.creatingItem"
    @submit="handleSubmit"
  />
</template>
